import { useEffect, useMemo, useState } from 'react';
import {
  composerMailTrackingTexte, decouperAdresses, LIBELLE_LANGUE, urlSuiviTransporteur,
} from '@mosaik/core';
import { ErreurApi } from '../api/client';
import { useDeposerBrouillon, useEnvoyerMailColis, useIgnorerMailColis, useReglages } from '../api/hooks';
import type { CommandeListee } from '../api/types';
import { IgnorerMailClient } from './IgnorerMailClient';
import { useToasts } from './Toasts';
import { Repousser } from './Repousser';

/**
 * Relecture du mail de tracking avant envoi.
 *
 * Ce mail partait tout seul à la saisie d'un numéro de suivi : composé, déposé,
 * et découvert dans Outlook. Il passe désormais par ici comme les autres —
 * aucun mail ne part sans avoir été vu, et cette fenêtre est la SEULE
 * relecture : « Envoyer » l'expédie depuis contact@, sans étape Outlook.
 *
 * Le corps est en texte : c'est ce qui se relit et se corrige. Le lien de suivi
 * y figure en clair et redevient cliquable à l'envoi, si bien que la relecture ne
 * coûte pas ce qui fait l'intérêt du message.
 *
 * Avec `colis`, c'est le mail d'un colis SUPPLÉMENTAIRE : composé sur son
 * numéro et son transporteur, il part sans toucher au mail du premier colis,
 * ni à l'étape — on ne l'écarte donc pas, et on ne repousse rien d'ici.
 */
export function MailTracking({
  commande, colis: numeroColis, onFermer,
}: {
  commande: CommandeListee;
  /** Numéro d'un colis supplémentaire ; absent, c'est le colis principal. */
  colis?: string;
  onFermer: () => void;
}) {
  const { ajouter } = useToasts();
  // Les réglages portent les modèles : sans eux, l'écran montrerait le texte
  // du code pendant que le serveur enverrait celui de Réglages.
  const { data: reglages } = useReglages();
  const deposer = useDeposerBrouillon();
  const envoyerColis = useEnvoyerMailColis();
  const ignorerColis = useIgnorerMailColis();
  const enCours = deposer.isPending || envoyerColis.isPending;
  const colis = numeroColis ? commande.colis.find((c) => c.tracking === numeroColis) ?? null : null;

  const modele = useMemo(() => composerMailTrackingTexte(colis ? {
    ref: commande.ref,
    tracking: colis.tracking,
    urlSuivi: colis.suivi,
    transporteur: colis.transporteur,
    langue: commande.langue,
  } : {
    ref: commande.ref,
    tracking: commande.tracking ?? '',
    // Le lien du transporteur, déduit du numéro, avec repli sur celui stocké.
    urlSuivi: urlSuiviTransporteur(commande.transporteur, commande.tracking) ?? commande.liens.suivi,
    transporteur: commande.transporteur,
    langue: commande.langue,
  }, reglages), [commande, colis, reglages]);

  const [to, setTo] = useState(commande.email ?? '');
  const [objet, setObjet] = useState(modele.objet);
  const [corps, setCorps] = useState(modele.corps);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    setTo(commande.email ?? ''); setObjet(modele.objet); setCorps(modele.corps);
  }, [modele, commande.email]);

  useEffect(() => {
    const auClavier = (e: KeyboardEvent) => { if (e.key === 'Escape') onFermer(); };
    document.addEventListener('keydown', auClavier);
    return () => document.removeEventListener('keydown', auClavier);
  }, [onFermer]);

  async function envoyer() {
    setErreur(null);
    try {
      const texte = { destinataire: to, objet, message: corps };
      // Sur le numéro demandé, pas sur le colis retrouvé : un colis retiré
      // entre-temps doit échouer, pas partir comme mail du premier colis.
      const r = numeroColis
        ? await envoyerColis.mutateAsync({ id: commande.id, tracking: numeroColis, corps: texte })
        : await deposer.mutateAsync({ id: commande.id, canal: 'tracking', corps: texte });

      if (!r.brouillon.depose) {
        setErreur(r.brouillon.erreur ?? "Le mail n'a pas pu être envoyé.");
        return;
      }
      ajouter(
        r.brouillon.simule
          ? 'Mail composé, mais Outlook est en mode local : aucun mail envoyé.'
          : `Mail de tracking${colis ? ` du colis ${colis.tracking}` : ''} envoyé à ${commande.client}.`,
        r.brouillon.simule ? 'bad' : 'ok',
      );
      onFermer();
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : "L'envoi a échoué.");
    }
  }

  return (
    <div className="modal" onClick={(e) => { if (e.target === e.currentTarget) onFermer(); }}>
      <div className="box" role="dialog" aria-modal="true" aria-label={`Mail de tracking ${commande.ref}`}>
        <div className="mhead">
          <h2>Mail de tracking — {commande.ref}{colis && <> · colis {colis.tracking}</>}</h2>
          <button className="x" onClick={onFermer} aria-label="Fermer">&times;</button>
        </div>

        <div className="mbody">
          <div className="field">
            <label htmlFor="mt-to">
              Destinataire
              <span className="tag" style={{ marginLeft: 8 }}>{LIBELLE_LANGUE[modele.langue]}</span>
            </label>
            <input
              id="mt-to" type="text" value={to} placeholder="adresse du client"
              onChange={(e) => setTo(e.target.value)}
            />
            {!commande.email && (
              <p className="err">
                Cette commande n&apos;a pas d&apos;adresse client : saisissez-la, sans quoi le
                mail ne peut pas partir.
              </p>
            )}
          </div>

          <div className="field">
            <label htmlFor="mt-obj">Objet</label>
            <input id="mt-obj" value={objet} onChange={(e) => setObjet(e.target.value)} />
          </div>

          <div className="field">
            <label htmlFor="mt-corps">Message</label>
            <textarea id="mt-corps" rows={14} value={corps} onChange={(e) => setCorps(e.target.value)} />
            <p className="indice">
              L&apos;adresse de suivi redevient un lien cliquable dans le mail envoyé.
            </p>
          </div>

          {erreur && <p className="err" role="alert">{erreur}</p>}

          <p style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
            Le mail <strong>part dès que vous cliquez sur Envoyer</strong>, depuis contact@ :
            relisez-le ici, il n&apos;y a pas d&apos;autre relecture.
          </p>
        </div>

        <div className="mfoot">
          {!colis && <Repousser commande={commande} />}
          <button type="button" className="btn" onClick={onFermer}>Annuler</button>
          {/* Prévenir n'est pas obligatoire : l'étape peut être tenue pour
              faite sans mail, depuis la fenêtre même où l'on en décide. */}
          {!colis && <IgnorerMailClient commande={commande} canal="tracking" onFait={onFermer} />}
          {/* Le pendant, pour un colis : « Prévenir le client du colis » s'éteint. */}
          {colis && colis.mailEtat !== 'envoye' && colis.mailEtat !== 'ignore' && (
            <button
              type="button" className="btn" disabled={ignorerColis.isPending}
              title="Le client ne sera pas prévenu de ce colis. Le mail reste possible depuis la fiche."
              onClick={async () => {
                try {
                  await ignorerColis.mutateAsync({ id: commande.id, tracking: colis.tracking });
                  ajouter(`${commande.ref} : colis ${colis.tracking} sans mail au client.`);
                  onFermer();
                } catch (e) {
                  setErreur(e instanceof ErreurApi ? e.message : 'Le mail n’a pas pu être écarté.');
                }
              }}
            >
              Ne pas prévenir
            </button>
          )}
          <button
            type="button" className="btn pri" disabled={enCours}
            onClick={() => void envoyer()}
          >
            {enCours ? <><span className="spin" /> Envoi…</> : 'Envoyer'}
          </button>
        </div>
      </div>
    </div>
  );
}
